import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Loading from "../components/Loading";
import FavHeart from "../components/FavHeart";
import ShareButton from "../components/ShareButton";
import { ArrowLeft, ExternalLink, User } from "lucide-react";
import { formatDate } from "../lib/format";

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b border-[color:var(--sat-border)] py-2">
      <span className="sat-label">{label}</span>
      <span className="font-mono text-sm text-[color:var(--sat-text)]">{value ?? "—"}</span>
    </div>
  );
}

export default function AstronautDetailPage() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/astronauts/${id}`)
      .then(r => setA(r.data))
      .catch(() => setA(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading message="ACCESSING PILOT DOSSIER" />;
  if (!a) return <div className="sat-label text-center py-16" data-testid="astronaut-not-found">RECORD NOT FOUND</div>;

  return (
    <div className="pb-10" data-testid="page-astronaut-detail">
      <Link to="/roster" className="sat-label inline-flex items-center gap-2 mb-6 hover:sat-cyan" data-testid="back-roster">
        <ArrowLeft size={14} /> BACK TO ROSTER
      </Link>

      <div className="sat-surface sat-brackets p-6 md:p-10 mb-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-black flex items-center justify-center overflow-hidden min-h-[240px]">
          {a.profile_image ? <img src={a.profile_image} alt={a.name} className="max-h-80 object-cover w-full h-full"/> : <User size={60} className="text-[color:var(--sat-border-bright)]" />}
        </div>
        <div>
          <div className="sat-label sat-cyan mb-2">{a.agency?.name?.toUpperCase() || "INDEPENDENT"}</div>
          <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight" data-testid="astronaut-name">{a.name}</h1>
          <div className="sat-label mt-3">
            {a.nationality} {a.country_code && `· ${a.country_code}`} {a.age !== null && a.age !== undefined && `· AGE ${a.age}`}
          </div>
          {a.bio && <p className="text-sm text-[color:var(--sat-text-dim)] mt-4 leading-relaxed line-clamp-6">{a.bio}</p>}
          <div className="flex flex-wrap gap-3 mt-6">
            <FavHeart kind="astronaut" ref_id={a.id} title={a.name} image={a.profile_image} size={14} testid="astronaut-fav" />
            <ShareButton url={typeof window !== "undefined" ? window.location.href : ""} title={a.name} size={14} testid="astronaut-share" />
            <span className="border border-[color:var(--sat-cyan)] px-3 py-2 font-mono text-[10px] tracking-widest sat-cyan">
              {a.in_space ? "IN ORBIT" : (a.status?.name?.toUpperCase() || "GROUNDED")}
            </span>
            {a.type?.name && (
              <span className="border border-[color:var(--sat-border-bright)] px-3 py-2 font-mono text-[10px] tracking-widest text-[color:var(--sat-text-dim)]">
                {a.type.name.toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <section className="sat-surface p-6" data-testid="astronaut-vitals">
          <div className="sat-label sat-cyan mb-4">VITALS</div>
          <Row label="DATE OF BIRTH" value={a.date_of_birth} />
          {a.date_of_death && <Row label="DATE OF DEATH" value={a.date_of_death} />}
          <Row label="NATIONALITY" value={a.nationality} />
          <Row label="STATUS" value={a.status?.name} />
          <Row label="AGENCY" value={a.agency?.name} />
        </section>
        <section className="sat-surface p-6" data-testid="astronaut-record">
          <div className="sat-label sat-cyan mb-4">FLIGHT RECORD</div>
          <Row label="TOTAL FLIGHTS" value={a.flights_count} />
          <Row label="LANDINGS" value={a.landings_count} />
          <Row label="SPACEWALKS" value={a.spacewalks_count} />
          <Row label="TIME IN SPACE" value={a.time_in_space || null} />
          <Row label="EVA TIME" value={a.eva_time || null} />
          <Row label="FIRST FLIGHT" value={a.first_flight ? formatDate(a.first_flight) : null} />
          <Row label="LAST FLIGHT" value={a.last_flight ? formatDate(a.last_flight) : null} />
        </section>
      </div>

      {(a.wiki || a.social_media_links?.length > 0) && (
        <section className="sat-surface p-6" data-testid="astronaut-resources">
          <div className="sat-label sat-cyan mb-4">RESOURCES</div>
          <div className="flex flex-wrap gap-3">
            {a.wiki && <a href={a.wiki} target="_blank" rel="noopener noreferrer" className="sat-btn" data-testid="astronaut-wiki">WIKIPEDIA <ExternalLink size={11}/></a>}
            {(a.social_media_links || []).slice(0, 4).map((s, i) => (
              <a key={i} href={s.url || s} target="_blank" rel="noopener noreferrer" className="sat-btn" data-testid={`astronaut-social-${i}`}>
                {(s.social_media?.name || "LINK").toUpperCase()} <ExternalLink size={11}/>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
